// Aus Payments, Flag, Payment.plan und Admin-Override wird ein
// Entitlements-Objekt. Alle Sperren im Dashboard lesen nur dieses Objekt.
// Rein, ohne React und ohne helpers/index.js.
//
// Modi
// legacy heißt kein Plan-Payment. Alles läuft wie heute, auch bei Schalter an.
// preview heißt Plan-Payment und Schalter aus. Die alten Gates kommen weiter
// vom legacyModePayment, Plan & Billing zeigt schon Paket und Nutzung.
// enforced heißt Plan-Payment und Schalter an. Gates und Limits kommen vom
// Plan-Payment.
import { LIMIT_SOURCES, resolveLimitState, resolvePlanLimits } from './limits'
import {
  planOf,
  selectPayments,
  simulatePlanPayment
} from './payment-selection'
import {
  CUSTOM_PLAN,
  FEATURES,
  FEATURE_KEYS,
  LEGACY_FEATURE_KEYS,
  PLANS,
  PLAN_ORDER,
  addonPlans,
  catalogProductsOf,
  includedIn,
  minPlan
} from './plans'

export const MODES = {
  LEGACY: 'legacy',
  PREVIEW: 'preview',
  ENFORCED: 'enforced'
}

export const FEATURE_REASONS = {
  MISSING_PRODUCT: 'MISSING_PRODUCT',
  TRIAL: 'TRIAL'
}

export const ADMIN_ENFORCEMENT = { FLAG: 'flag', ON: 'on', OFF: 'off' }
export const REAL_PLAN = 'real'

export const DEFAULT_ADMIN_OVERRIDE = Object.freeze({
  enforcement: ADMIN_ENFORCEMENT.FLAG,
  plan: REAL_PLAN
})

const RESOURCE_KEYS = ['careers', 'seats', 'talentStorage']

// Unbekannte oder kaputte Werte fallen auf den Default zurück
export const normalizeAdminOverride = (override) => {
  const enforcement = Object.values(ADMIN_ENFORCEMENT).includes(
    override?.enforcement
  )
    ? override.enforcement
    : DEFAULT_ADMIN_OVERRIDE.enforcement
  const plan = PLAN_ORDER.includes(override?.plan)
    ? override.plan
    : DEFAULT_ADMIN_OVERRIDE.plan
  return { enforcement, plan }
}

// Der Admin-Override wirkt nur bei Admins. Sonst entscheidet das Flag, und
// nicht geladen oder Fehler heißt aus.
export const resolveSwitch = ({ flagOn, flagsLoaded, isAdmin, override }) => {
  if (isAdmin && override.enforcement === ADMIN_ENFORCEMENT.ON)
    return { on: true, source: 'admin' }
  if (isAdmin && override.enforcement === ADMIN_ENFORCEMENT.OFF)
    return { on: false, source: 'admin' }
  return { on: !!flagOn && !!flagsLoaded, source: 'flag' }
}

const hasAnyProduct = (payment, products) => {
  const booked = payment?.products || []
  return products.some((product) => booked.includes(product))
}

// products ist das Gate, catalogProducts das, was die LP verkauft. Badge und
// Upgrade-Hinweis lesen includedIn, addonIn und minPlan aus dem Katalog.
const catalogOf = (feature) => ({
  key: feature.key,
  product: feature.products[0],
  products: feature.products,
  catalogProducts: catalogProductsOf(feature.key),
  i18nKey: feature.i18nKey,
  legacyKey: feature.legacyKey,
  includedIn: includedIn(feature.key),
  addonIn: addonPlans(feature.key),
  minPlan: minPlan(feature.key)
})

const featureState = ({ feature, enabled, gated, reason, source, inPlan }) => ({
  ...catalogOf(feature),
  enabled,
  locked: !enabled,
  inPlan,
  gated,
  reason: enabled ? null : reason,
  source
})

// Die fünf Bestands-Gates in legacy und preview exakt wie bisher in
// payment-context.js. INTEGRATIONS ist bei einem Trial immer aus.
const legacyGate = (feature, payment) => {
  if (
    LEGACY_FEATURE_KEYS[feature.key] === 'integrations' &&
    payment?.is_trial
  ) {
    return { enabled: false, reason: FEATURE_REASONS.TRIAL }
  }
  return {
    enabled: hasAnyProduct(payment, feature.products),
    reason: FEATURE_REASONS.MISSING_PRODUCT
  }
}

export const resolveFeatures = ({ mode, legacyModePayment, planPayment }) =>
  Object.fromEntries(
    FEATURE_KEYS.map((key) => {
      const feature = FEATURES[key]
      const inPlan = planPayment
        ? hasAnyProduct(planPayment, feature.products)
        : null

      if (mode === MODES.ENFORCED) {
        return [
          key,
          featureState({
            feature,
            enabled: inPlan === true,
            gated: true,
            reason: FEATURE_REASONS.MISSING_PRODUCT,
            source: 'plan',
            inPlan
          })
        ]
      }

      // Neue Gates sperren nur in enforced
      if (!LEGACY_FEATURE_KEYS[key]) {
        return [
          key,
          featureState({
            feature,
            enabled: true,
            gated: false,
            reason: null,
            source: 'legacy',
            inPlan
          })
        ]
      }

      const { enabled, reason } = legacyGate(feature, legacyModePayment)
      return [
        key,
        featureState({
          feature,
          enabled,
          gated: true,
          reason,
          source: 'legacy',
          inPlan
        })
      ]
    })
  )

const unknownLimit = (resource, source) =>
  resolveLimitState({ resource, limit: null, enforced: false, source })

// Ohne Plan-Payment gibt es keine Limits, Legacy rechnet weiter über
// usePaymentRestrictions. used bleibt hier null, die Hooks setzen die echte
// Nutzung über resolveLimitState ein.
export const resolveLimits = ({ mode, planId, planPayment }) => {
  if (!planPayment) {
    return Object.fromEntries(
      RESOURCE_KEYS.map((resource) => [
        resource,
        unknownLimit(resource, LIMIT_SOURCES.LEGACY)
      ])
    )
  }

  const resolved = resolvePlanLimits({ planId, payment: planPayment })
  return Object.fromEntries(
    RESOURCE_KEYS.map((resource) => [
      resource,
      resolveLimitState({
        resource,
        limit: resolved[resource].limit,
        used: null,
        enforced: mode === MODES.ENFORCED,
        source: resolved[resource].source
      })
    ])
  )
}

const planInfo = (planId) => {
  if (!planId) return null
  const definition = PLANS[planId] || null
  return {
    id: planId,
    name: definition ? definition.name : null,
    isCustom: planId === CUSTOM_PLAN,
    definition
  }
}

export const resolveEntitlements = ({
  payments = [],
  planById = {},
  planFieldSettled = true,
  flagOn = false,
  flagsLoaded = false,
  isAdmin = false,
  adminOverride = DEFAULT_ADMIN_OVERRIDE
} = {}) => {
  const override = isAdmin
    ? normalizeAdminOverride(adminOverride)
    : DEFAULT_ADMIN_OVERRIDE

  const selection = selectPayments(payments, planById)
  const switchState = resolveSwitch({ flagOn, flagsLoaded, isAdmin, override })

  // Simulation nur für Admins und nur, wenn es ein echtes Payment als Basis
  // gibt. Die Laufzeit (periodPayment) bleibt immer die echte.
  const simulationBase = selection.planPayment || selection.legacyModePayment
  const simulatedPayment =
    isAdmin && override.plan !== REAL_PLAN && simulationBase
      ? simulatePlanPayment({ base: simulationBase, planId: override.plan })
      : null

  const planPayment = simulatedPayment || selection.planPayment
  const planId = simulatedPayment
    ? override.plan
    : planOf(selection.planPayment, planById)

  let mode = MODES.LEGACY
  if (planPayment) mode = switchState.on ? MODES.ENFORCED : MODES.PREVIEW

  const payment =
    mode === MODES.ENFORCED ? planPayment : selection.legacyModePayment

  const settled =
    (!!flagsLoaded ||
      (isAdmin && override.enforcement !== ADMIN_ENFORCEMENT.FLAG)) &&
    !!planFieldSettled

  return {
    mode,
    isEnforced: mode === MODES.ENFORCED,
    settled,
    // Solange das Plan-Feld lädt, ist ein Plan-Payment noch nicht erkennbar
    // und mode steht vorläufig auf legacy. Der Talentspeicher maskiert dann.
    planFieldSettled: !!planFieldSettled,
    isSimulated: !!simulatedPayment,
    isAdmin: !!isAdmin,
    override,
    switch: switchState,
    plan: planPayment ? planInfo(planId) : null,
    payment: payment || null,
    periodPayment: selection.periodPayment,
    legacyPayment: selection.legacyPayment,
    legacyModePayment: selection.legacyModePayment,
    planPayment: planPayment || null,
    realPlanPayment: selection.planPayment,
    // Echtes Paket, auch während einer Simulation (Admin-Dialog)
    realPlan: selection.planPayment
      ? planInfo(planOf(selection.planPayment, planById))
      : null,
    ignoredPlanPayments: selection.ignored,
    limits: resolveLimits({ mode, planId, planPayment }),
    features: resolveFeatures({
      mode,
      legacyModePayment: selection.legacyModePayment,
      planPayment
    })
  }
}

// Fallback für Tests und Kontexte ohne Entitlements. Alles frei, Limits
// unbekannt.
export const LEGACY_ENTITLEMENTS = Object.freeze({
  mode: MODES.LEGACY,
  isEnforced: false,
  settled: true,
  planFieldSettled: true,
  isSimulated: false,
  isAdmin: false,
  override: DEFAULT_ADMIN_OVERRIDE,
  switch: Object.freeze({ on: false, source: 'flag' }),
  plan: null,
  payment: null,
  periodPayment: null,
  legacyPayment: null,
  legacyModePayment: null,
  planPayment: null,
  realPlanPayment: null,
  realPlan: null,
  ignoredPlanPayments: Object.freeze([]),
  limits: Object.freeze(
    Object.fromEntries(
      RESOURCE_KEYS.map((resource) => [
        resource,
        Object.freeze(unknownLimit(resource, LIMIT_SOURCES.LEGACY))
      ])
    )
  ),
  features: Object.freeze(
    Object.fromEntries(
      FEATURE_KEYS.map((key) => [
        key,
        Object.freeze(
          featureState({
            feature: FEATURES[key],
            enabled: true,
            gated: false,
            reason: null,
            source: 'legacy',
            inPlan: null
          })
        )
      ])
    )
  )
})
